import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

import PageHeader from '../components/shared/PageHeader';
import Card from '../components/ui/Card';
import Table from '../components/ui/Table';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';

import { supabase } from '../lib/supabase';
import { onlyDigits } from '../utils/masks';
import { isValidPlate } from '../utils/validators';
import { getVehicleYearOptions } from '../utils/vehicleYears';
import { useLocalization } from '../hooks/useLocalization';
import { distance } from '../utils/formatters';

export default function Veiculos() {
  const { settings } = useLocalization();

  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);

  const [open, setOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const emptyForm = {
    cliente_id: '',
    placa: '',
    marca: '',
    marca_custom: '',
    modelo: '',
    ano: '',
    cor: '',
    km_atual: '',
  };

  const [form, setForm] = useState(emptyForm);

  const marcas = [
    'Fiat',
    'Volkswagen',
    'Chevrolet',
    'Ford',
    'Toyota',
    'Honda',
    'Hyundai',
    'Renault',
    'Nissan',
    'Jeep',
  ];

  const cores = [
    'Branco',
    'Preto',
    'Prata',
    'Cinza',
    'Vermelho',
    'Azul',
    'Verde',
    'Amarelo',
    'Outra',
  ];

  const anos = getVehicleYearOptions();

  const getOficinaId = async () => {
    const { data: authData, error: authError } =
      await supabase.auth.getUser();

    if (authError) {
      console.error('Erro ao obter usuário:', authError);
      return null;
    }

    if (!authData?.user?.email) {
      return null;
    }

    const { data: userRow, error: userError } = await supabase
      .from('usuarios')
      .select('oficina_id')
      .eq('email', authData.user.email)
      .maybeSingle();

    if (userError) {
      console.error('Erro ao obter oficina:', userError);
      return null;
    }

    return userRow?.oficina_id || null;
  };

  const load = async () => {
    try {
      const oficina_id = await getOficinaId();

      if (!oficina_id) {
        return;
      }

      const [veic, cli] = await Promise.all([
        supabase
          .from('veiculos')
          .select('*')
          .eq('oficina_id', oficina_id)
          .is('removed_at', null)
          .order('criado_em', { ascending: false }),

        supabase
          .from('clientes')
          .select('id,nome')
          .eq('oficina_id', oficina_id)
          .is('removed_at', null),
      ]);

      if (veic.error) {
        console.error('Erro ao carregar veículos:', veic.error);
      }

      if (cli.error) {
        console.error('Erro ao carregar clientes:', cli.error);
      }

      setRows(veic.data || []);
      setClients(cli.data || []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
    }
  };

  useEffect(() => {
    load();

    // Initial tenant-scoped vehicle load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (saving) return;

    const marcaFinal =
      form.marca === '__other__'
        ? form.marca_custom.trim()
        : form.marca.trim();

    if (!form.cliente_id) {
      toast.error('Selecione o cliente.');
      return;
    }

    if (!form.placa.trim()) {
      toast.error(
        settings.countryCode === 'BR'
          ? 'Informe a placa do veículo.'
          : `Informe o ${settings.vehicleRegistrationLabel}.`
      );
      return;
    }

    if (!isValidPlate(form.placa, settings.countryCode)) {
      toast.error(
        settings.countryCode === 'BR'
          ? 'Informe uma placa válida.'
          : `Informe um ${settings.vehicleRegistrationLabel} válido.`
      );
      return;
    }

    if (!marcaFinal) {
      toast.error('Informe a marca do veículo.');
      return;
    }

    try {
      setSaving(true);

      const oficina_id = await getOficinaId();

      if (!oficina_id) {
        toast.error(
          'Não foi possível identificar a oficina. Tente novamente.'
        );
        return;
      }

      const { error } = await supabase
        .from('veiculos')
        .insert({
          cliente_id: form.cliente_id,
          placa: form.placa.trim().toUpperCase(),
          marca: marcaFinal,
          modelo: form.modelo.trim(),
          cor: form.cor.trim(),
          oficina_id,
          ano: form.ano ? Number(form.ano) : null,
          km_atual: form.km_atual
            ? Number(form.km_atual)
            : null,
          odometro_unidade:
            settings.measurementSystem === 'imperial'
              ? 'mi'
              : 'km',
        });

      if (error) {
        console.error('Erro ao cadastrar veículo:', error);

        toast.error(
          'Não foi possível cadastrar o veículo. Verifique os dados e tente novamente.'
        );

        return;
      }

      toast.success('Veículo cadastrado com sucesso!');

      setOpen(false);
      setForm(emptyForm);

      await load();
    } catch (error) {
      console.error('Erro ao cadastrar veículo:', error);

      toast.error(
        'Não foi possível cadastrar o veículo. Tente novamente.'
      );
    } finally {
      setSaving(false);
    }
  };

  const askRemoveVehicle = (vehicle) => {
    setVehicleToDelete(vehicle);
    setDeleteOpen(true);
  };

  const removeVehicle = async () => {
    if (!vehicleToDelete || removing) return;

    try {
      setRemoving(true);

      const { error } = await supabase.rpc('remove_veiculo', {
        target_veiculo_id: vehicleToDelete.id,
      });

      if (error) {
        console.error('Erro ao remover veículo:', error);

        toast.error(
          'Não foi possível remover o veículo. Tente novamente.'
        );

        return;
      }

      toast.success('Veículo removido com sucesso!');

      setDeleteOpen(false);
      setVehicleToDelete(null);

      await load();
    } catch (error) {
      console.error('Erro ao remover veículo:', error);

      toast.error(
        'Não foi possível remover o veículo. Tente novamente.'
      );
    } finally {
      setRemoving(false);
    }
  };

  const closeCreateModal = () => {
    if (saving) return;

    setOpen(false);
    setForm(emptyForm);
  };

  const closeDeleteModal = () => {
    if (removing) return;

    setDeleteOpen(false);
    setVehicleToDelete(null);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Veículos"
        subtitle="Gestão de veículos da oficina"
        actions={
          <Button
            onClick={() => {
              setForm(emptyForm);
              setOpen(true);
            }}
          >
            <Plus size={15} />
            Novo veículo
          </Button>
        }
      />

      <Card>
        <Table
          columns={[
            'Placa',
            'Marca',
            'Modelo',
            'Ano',
            'Cor',
            'KM',
            'Cliente',
            'Ações',
          ]}
          rows={rows || []}
          renderRow={(v) => (
            <tr
              key={v.id}
              className="border-t border-border hover:bg-surface2"
            >
              <td className="px-3 py-2 font-mono text-sm">
                {v.placa}
              </td>

              <td className="px-3 py-2">
                {v.marca || '-'}
              </td>

              <td className="px-3 py-2">
                {v.modelo || '-'}
              </td>

              <td className="px-3 py-2">
                {v.ano || '-'}
              </td>

              <td className="px-3 py-2">
                {v.cor || '-'}
              </td>

              <td className="px-3 py-2">
                {v.km_atual || v.km
                  ? distance(v.km_atual || v.km)
                  : '-'}
              </td>

              <td className="px-3 py-2">
                {(clients || []).find(
                  (c) => c.id === v.cliente_id
                )?.nome || '-'}
              </td>

              <td className="px-3 py-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-400"
                  onClick={() => askRemoveVehicle(v)}
                >
                  <Trash2 size={14} />
                  Remover
                </Button>
              </td>
            </tr>
          )}
        />
      </Card>

      <Modal
        open={open}
        onClose={closeCreateModal}
        title="Novo Veículo"
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-400">
              Cliente *
            </label>

            <Select
              value={form.cliente_id}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  cliente_id: e.target.value,
                }))
              }
              options={[
                {
                  value: '',
                  label: 'Selecione o cliente',
                },
                ...(clients || []).map((c) => ({
                  value: c.id,
                  label: c.nome,
                })),
              ]}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-400">
              {settings.vehicleRegistrationLabel} *
            </label>

            <Input
              placeholder={
                settings.vehicleRegistrationLabel
              }
              mask="plate"
              value={form.placa}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  placa: e.target.value,
                }))
              }
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-400">
                Marca *
              </label>

              <Select
                value={form.marca}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    marca: e.target.value,
                    marca_custom:
                      e.target.value === '__other__'
                        ? p.marca_custom
                        : '',
                  }))
                }
                options={[
                  {
                    value: '',
                    label: 'Selecione',
                  },
                  ...marcas.map((m) => ({
                    value: m,
                    label: m,
                  })),
                  {
                    value: '__other__',
                    label: 'Outra marca',
                  },
                ]}
              />
            </div>

            {form.marca === '__other__' && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-400">
                  Qual marca? *
                </label>

                <Input
                  placeholder="Digite a marca do veículo"
                  value={form.marca_custom}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      marca_custom: e.target.value,
                    }))
                  }
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-400">
                Modelo
              </label>

              <Input
                placeholder="Ex: Uno, Gol, Onix"
                value={form.modelo}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    modelo: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-400">
                Ano
              </label>

              <Select
                value={form.ano}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ano: e.target.value,
                  }))
                }
                options={[
                  {
                    value: '',
                    label: 'Selecione',
                  },
                  ...anos.map((a) => ({
                    value: a,
                    label: a,
                  })),
                ]}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-400">
                Cor
              </label>

              <Select
                value={form.cor}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    cor: e.target.value,
                  }))
                }
                options={[
                  {
                    value: '',
                    label: 'Selecione',
                  },
                  ...cores.map((c) => ({
                    value: c,
                    label: c,
                  })),
                ]}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-400">
              Odômetro atual (
              {settings.measurementSystem === 'imperial'
                ? 'mi'
                : 'km'}
              )
            </label>

            <Input
              placeholder="Ex: 45000"
              mask="integer"
              value={form.km_atual}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  km_atual: onlyDigits(
                    e.target.value,
                    7
                  ),
                }))
              }
            />
          </div>

          <Button
            onClick={save}
            disabled={saving}
          >
            {saving
              ? 'Salvando...'
              : 'Salvar'}
          </Button>
        </div>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={closeDeleteModal}
        title="Confirmar remoção"
      >
        <div className="space-y-4">
          <p className="text-zinc-300">
            Deseja remover o veículo{' '}
            <strong>
              {vehicleToDelete?.placa}
            </strong>
            ?
          </p>

          <p className="text-sm text-zinc-500">
            Ele sairá das listagens, mantendo o histórico
            vinculado.
          </p>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              onClick={closeDeleteModal}
              disabled={removing}
            >
              Cancelar
            </Button>

            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={removeVehicle}
              disabled={removing}
            >
              {removing
                ? 'Removendo...'
                : 'Remover veículo'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}